/**
 * ThemeToggle.jsx
 * Compact theme button for the navbar and mobile drawer.
 */

import { AnimatePresence, motion as Motion } from 'framer-motion';
import { Sun, Moon } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const BUTTON_CSS = `
  .tt-button {
    position: relative;
    width: 40px;
    height: 40px;
    border-radius: 999px;
    border: 1px solid var(--line-strong);
    background:
      radial-gradient(circle at 30% 30%, rgba(255,255,255,0.2), transparent 58%),
      var(--surface-glass, rgba(0, 229, 255, 0.08));
    color: var(--accent);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,0.16),
      0 0 18px rgba(0, 229, 255, 0.12);
    transition:
      border-color 0.25s ease,
      box-shadow 0.25s ease,
      transform 0.25s ease,
      background 0.25s ease;
  }
  .tt-button:hover {
    border-color: var(--accent);
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,0.2),
      0 0 22px rgba(0, 229, 255, 0.18);
    transform: translateY(-1px);
  }
  .tt-button:focus-visible {
    outline: none;
    border-color: var(--accent);
    box-shadow:
      0 0 0 3px color-mix(in srgb, var(--accent) 24%, transparent),
      0 0 22px rgba(0, 229, 255, 0.2);
  }
  .tt-button__glow {
    position: absolute;
    inset: 7px;
    border-radius: inherit;
    background: radial-gradient(circle, rgba(0, 229, 255, 0.18) 0%, transparent 72%);
    opacity: 0.9;
    pointer-events: none;
  }
  .tt-button__icon {
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }
  [data-theme="light"] .tt-button {
    background:
      radial-gradient(circle at 30% 30%, rgba(255,255,255,0.3), transparent 58%),
      rgba(180, 100, 10, 0.08);
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,0.3),
      0 0 18px rgba(180, 100, 10, 0.14);
  }
  [data-theme="light"] .tt-button:hover {
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,0.34),
      0 0 22px rgba(180, 100, 10, 0.18);
  }
  [data-theme="light"] .tt-button__glow {
    background: radial-gradient(circle, rgba(180, 100, 10, 0.18) 0%, transparent 72%);
  }
`;

export default function ThemeToggle() {
  const { toggleTheme, isDark } = useTheme();
  const nextTheme = isDark ? 'light' : 'dark';

  return (
    <>
      <style>{BUTTON_CSS}</style>
      <Motion.button
        type="button"
        className="tt-button no-transition"
        onClick={toggleTheme}
        aria-label={`Switch to ${nextTheme} mode`}
        title={`Switch to ${isDark ? 'Amber Mirage (light)' : 'Sapphire Veil (dark)'}`}
        whileTap={{ scale: 0.94 }}
      >
        <span className="tt-button__glow" aria-hidden="true" />
        <AnimatePresence mode="wait" initial={false}>
          <Motion.span
            key={isDark ? 'sun' : 'moon'}
            className="tt-button__icon"
            initial={{ opacity: 0, rotate: -24, scale: 0.78 }}
            animate={{ opacity: 1, rotate: 0, scale: 1 }}
            exit={{ opacity: 0, rotate: 24, scale: 0.78 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          >
            {isDark ? (
              <Sun size={17} strokeWidth={2} aria-hidden="true" />
            ) : (
              <Moon size={17} strokeWidth={2} aria-hidden="true" />
            )}
          </Motion.span>
        </AnimatePresence>
      </Motion.button>
    </>
  );
}
